import { ReactNode } from "react"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faGripVertical, faTrash, faList, faStar } from "@fortawesome/free-solid-svg-icons"
import { t } from "i18next"
import TextInput from "@/components/text-input"
import ListButton from "@/components/list-button"
import { RiskAttribute } from "@/redux/account.types"

interface Props {
  items: RiskAttribute[]
  onChange: (items: RiskAttribute[]) => void
  onRemove: (item: RiskAttribute) => void
  onAdd?: () => void
  familyColumnId?: string | null
  onSelectFamilyColumn?: (id: string | null) => void
  renderSource?: (item: RiskAttribute) => ReactNode
}

export default function RiskAttributesEditor({ items, onChange, onRemove, onAdd, renderSource, familyColumnId, onSelectFamilyColumn }: Props) {
  return (
    <DragDropContext onDragEnd={({ source, destination }) => {
      if (!destination) return
      const next = [...items]
      const [moved] = next.splice(source.index, 1)
      next.splice(destination.index, 0, moved)
      onChange(next)
    }}>
      <Droppable droppableId="risk-attributes">
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps} className="w-full" onClick={(event) => event.stopPropagation()}>
            {items.map((item, index) => (
              <Draggable key={item.id} draggableId={item.id} index={index}>
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.draggableProps} className="flex items-center gap-[12px] py-[12px]">
                    <span {...provided.dragHandleProps} className="cursor-grab text-gray-400" aria-label="Déplacer la colonne">
                      <FontAwesomeIcon icon={faGripVertical} />
                    </span>
                    <div className="flex flex-1 gap-[24px] min-w-0">
                      {renderSource && <div className="flex-1 min-w-[200px] my-auto">{renderSource(item)}</div>}
                      <div className="flex-1 min-w-[200px]">
                        <TextInput value={item.name} isFullWidth onChange={({ value }) =>
                          onChange(items.map((current) => current.id === item.id ? { ...current, name: value } : current))
                        } />
                      </div>
                    </div>
                    {onSelectFamilyColumn && <ListButton
                      icon={faStar}
                      color={familyColumnId === item.id ? "#eab308" : undefined}
                      isOutlineColor={familyColumnId === item.id}
                      text={familyColumnId === item.id ? "Colonne des familles de risques — cliquer pour désactiver" : "Utiliser cette colonne comme familles de risques"}
                      onClick={() => onSelectFamilyColumn(familyColumnId === item.id ? null : item.id)}
                    />}
                    <ListButton icon={faTrash} text={t("utils_delete")} onClick={() => onRemove(item)} />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
            {onAdd && <div className="text-right"><ListButton plus icon={faList} text={t("Ajouter un attribut")} onClick={onAdd} /></div>}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  )
}
